import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import {
  TRIAL_DAYS_DEFAULT,
  TRIAL_DAYS_MAX,
  TRIAL_DAYS_MIN,
  type SaasBankTransfer,
  type SaasBankTransferUsd,
  type SaasSelfServiceConfig,
  type SaasTrialConfig,
  type SaasTrialMode,
  TRIAL_MODES,
  TRIAL_MODE_LABEL,
} from "@/lib/saas-billing-config";
import {
  DEFAULT_OPERATION_METHOD_PRIORITY,
  SAAS_CHECKOUT_METHOD_LABELS,
  eligibleProvidersFor,
  selfServiceCanBeEnabled,
  type SaasBillingOperation,
  type SaasCheckoutPaymentMethod,
  type SaasProviderAvailability,
  type SaasRoutableProvider,
} from "@/lib/saas-payment-routing";
import { AdminGlassPanel } from "./AdminGlassPanel";
import { useAppDialogs } from "@/components/ui/app-dialogs";

interface Config {
  annualDiscountPct: number;
  bankTransferArs?: SaasBankTransfer | null;
  bankTransferUsd?: SaasBankTransferUsd | null;
  selfService?: SaasSelfServiceConfig;
  trial?: SaasTrialConfig;
}

/** SCRUM-225 — secciones del hub Cobros (pricing de planes NO vive acá). */
export type SaasBillingConfigSection = "self-service" | "transfer" | "routing" | "legacy-all";

function BankField({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Input className="h-8 text-xs" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}

const OPS: SaasBillingOperation[] = ["monthly_subscription", "annual", "pack"];
const METHODS: SaasCheckoutPaymentMethod[] = ["credit_card", "debit_card", "bank_transfer"];
const PROVIDER_LABEL: Record<SaasRoutableProvider, string> = {
  mercadopago: "Mercado Pago",
  uala: "Ualá Bis",
  payway: "Payway",
  dlocal: "dLocal Go",
  manual_transfer: "Transferencia",
};
const OP_LABEL: Record<SaasBillingOperation, string> = {
  monthly_subscription: "Mensual (recurrente)",
  annual: "Anual (12/24/48 vía planes)",
  pack: "Packs",
};

/** A dónde se configura cada pasarela (pestañas del admin). dLocal hoy se configura solo por variables de entorno. */
const PROVIDER_HUB: Record<SaasRoutableProvider, { tab?: string; note?: string }> = {
  mercadopago: { tab: "saas-mp" },
  payway: { tab: "cobros-payway" },
  uala: { tab: "cobros-uala" },
  dlocal: { note: "Se configura por variables de entorno (DLOCAL_*); no tiene pantalla." },
  manual_transfer: { tab: "cobros-transfer" },
};

export function SaasBillingConfigPanel({ section = "legacy-all" }: { section?: SaasBillingConfigSection }) {
  const [config, setConfig] = useState<Config>({ annualDiscountPct: 25 });
  const trialMode: SaasTrialMode = config.trial?.mode ?? "assisted";
  // SCRUM-1037: un solo lugar arma el trial completo (enabled/días/modo) para que approval siempre salga del modo.
  // Los días no se acotan acá (se acotan al guardar) para no pisar lo que se está tipeando.
  const patchTrial = (patch: Partial<Pick<SaasTrialConfig, "enabled" | "days" | "mode" | "verifyEmail" | "captcha">>) =>
    setConfig((c) => {
      const t = {
        enabled: c.trial?.enabled === true, days: c.trial?.days ?? TRIAL_DAYS_DEFAULT, mode: c.trial?.mode ?? "assisted",
        verifyEmail: c.trial?.verifyEmail !== false, captcha: c.trial?.captcha === true, ...patch,
      };
      return { ...c, trial: { ...t, approval: t.mode === "self_service" ? "auto" : "manual" } };
    });
  const [availability, setAvailability] = useState<SaasProviderAvailability | null>(null);
  const { confirm } = useAppDialogs();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiGetJson<{ config: Config; availability?: SaasProviderAvailability }>("/api/admin/billing-config");
      setConfig(r.config);
      setAvailability(r.availability ?? null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al cargar");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const patchArs = (k: keyof SaasBankTransfer, v: string) =>
    setConfig((c) => ({ ...c, bankTransferArs: { ...(c.bankTransferArs ?? { holder: "", cbu: "", alias: "", bank: "" }), [k]: v } }));

  const patchUsd = (k: keyof SaasBankTransferUsd, v: string) =>
    setConfig((c) => ({ ...c, bankTransferUsd: { ...(c.bankTransferUsd ?? { holder: "", cbu: "", alias: "", bank: "" }), [k]: v } }));

  const patchSelfService = (patch: Partial<SaasSelfServiceConfig> & { gateways?: Partial<SaasSelfServiceConfig["gateways"]> }) => {
    setConfig((c) => ({
      ...c,
      selfService: {
        enabled: patch.enabled ?? c.selfService?.enabled ?? true,
        gateways: {
          payway: patch.gateways?.payway ?? c.selfService?.gateways?.payway ?? true,
          uala: patch.gateways?.uala ?? c.selfService?.gateways?.uala ?? true,
          dlocal: patch.gateways?.dlocal ?? c.selfService?.gateways?.dlocal ?? true,
          manualTransferUsd: patch.gateways?.manualTransferUsd ?? c.selfService?.gateways?.manualTransferUsd ?? true,
          manualTransferArs: patch.gateways?.manualTransferArs ?? c.selfService?.gateways?.manualTransferArs ?? false,
        },
        subscriptionPreferredGateway:
          patch.subscriptionPreferredGateway ?? c.selfService?.subscriptionPreferredGateway ?? "mercadopago",
        packGatewayPriority: patch.packGatewayPriority ?? c.selfService?.packGatewayPriority ?? ["payway", "uala"],
        operationMethodPriority: patch.operationMethodPriority ?? c.selfService?.operationMethodPriority,
        multiYearPeriods: patch.multiYearPeriods ?? c.selfService?.multiYearPeriods ?? false,
      },
    }));
  };


  const setPriority = (op: SaasBillingOperation, method: SaasCheckoutPaymentMethod, ordered: SaasRoutableProvider[]) => {
    const cur = { ...(config.selfService?.operationMethodPriority ?? {}) };
    const opMap = { ...(cur[op] ?? {}) };
    opMap[method] = ordered;
    cur[op] = opMap;
    patchSelfService({ operationMethodPriority: cur });
  };

  const moveProvider = (
    op: SaasBillingOperation,
    method: SaasCheckoutPaymentMethod,
    provider: SaasRoutableProvider,
    dir: -1 | 1,
  ) => {
    const current =
      config.selfService?.operationMethodPriority?.[op]?.[method] ??
      DEFAULT_OPERATION_METHOD_PRIORITY[op][method];
    const idx = current.indexOf(provider);
    if (idx < 0) return;
    const next = [...current];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setPriority(op, method, next);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Persist existing annualDiscountPct without exposing UI (SoT sigue en API; planes gobiernan pricing).
      await apiSendJson("/api/admin/billing-config", "PATCH", config);
      toast.success("Configuración de cobros guardada");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="flex items-center gap-2 p-4 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</p>;
  }

  const ars = config.bankTransferArs;
  const usd = config.bankTransferUsd;
  const ss = config.selfService;
  const showSelf = section === "self-service" || section === "legacy-all";
  const showTransfer = section === "transfer" || section === "legacy-all";
  const showRouting = section === "routing" || section === "legacy-all";

  const title =
    section === "self-service" ? "Self-service"
      : section === "transfer" ? "Transferencia bancaria"
      : section === "routing" ? "Routing por operación y método"
      : "Configuración self-service";

  const description =
    section === "self-service"
      ? "Kill-switch de /contratar. Métodos y providers los define Routing + pasarelas conectadas."
      : section === "transfer"
        ? "Medio de cobro propio de Structura (no es pasarela de terceros)."
        : section === "routing"
          ? "Prioridad por operación × método. El checkout muestra métodos; el backend elige provider."
          : "Habilitá el alta online, pasarelas de pago y datos bancarios para transferencia.";

  return (
    <AdminGlassPanel title={title} description={description}>
    <div className="space-y-6">
      {showSelf ? (
      <div className="glass-card space-y-4 rounded-xl p-4">
        <p className="text-sm font-semibold">Alta online (/contratar)</p>
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium">Self-service habilitado</p>
            <p className="text-xs text-muted-foreground">
              Si está off, /contratar no inicia sesiones nuevas. No afecta tenants existentes ni webhooks de pagos ya iniciados.
            </p>
          </div>
          <Switch
            disabled={ss?.enabled === false && availability != null && !selfServiceCanBeEnabled(availability)}
            checked={ss?.enabled !== false}
            onCheckedChange={(v) => {
              void (async () => {
              if (v && ss?.enabled === false) {
                const ok = await confirm({
                  title: "¿Activar Alta online?",
                  description:
                    "Los clientes podrán pagar en /contratar con providers habilitados/configurados.\n"
                    + "No se crea tenant hasta que el proveedor confirme el pago.\n"
                    + "Tenants existentes no se modifican.",
                  confirmLabel: "Activar",
                });
                if (!ok) return;
              }
              patchSelfService({ enabled: v });
              })();
            }}
          />
        </div>
        {ss?.enabled === false && availability != null && !selfServiceCanBeEnabled(availability) ? (
          <p className="text-xs text-amber-600">
            No se puede activar: ningún proveedor está habilitado y configurado. Conectá al menos una pasarela o cargá la cuenta bancaria.
          </p>
        ) : null}
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium">Planes de 24 y 48 meses</p>
            <p className="text-xs text-muted-foreground">
              Ofrece en el alta los mismos períodos que la página de precios, con el descuento de cada rubro.
              Mensual y 12 meses están siempre.
            </p>
          </div>
          <Switch
            checked={ss?.multiYearPeriods === true}
            aria-label="Planes de 24 y 48 meses en el alta online"
            onCheckedChange={(v) => patchSelfService({ multiYearPeriods: v })}
          />
        </div>
        <div className="space-y-3 rounded-lg border border-border/50 p-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium">Prueba gratis</p>
              <p className="text-xs text-muted-foreground">
                Muestra "Prueba gratis" en /contratar y en los precios de cada rubro. Independiente del alta paga.
                {trialMode === "self_service"
                  ? " La cuenta se crea apenas la persona la pide, sin tu revisión."
                  : " Cada solicitud queda pendiente en la bandeja del backoffice; no se crea ninguna cuenta sin tu aprobación."}
              </p>
            </div>
            <Switch
              checked={config.trial?.enabled === true}
              aria-label="Prueba gratis habilitada"
              onCheckedChange={(v) => {
                void (async () => {
                  if (v) {
                    const ok = await confirm({
                      title: "¿Activar la prueba gratis?",
                      description:
                        `Cualquier visitante podrá pedir ${config.trial?.days ?? TRIAL_DAYS_DEFAULT} días gratis desde el sitio.\n`
                        + (trialMode === "self_service"
                          ? "Modo self-service: la cuenta se crea en el momento, sin aprobación."
                          : "Las solicitudes quedan pendientes de tu aprobación (bandeja de pruebas del backoffice)."),
                      confirmLabel: "Activar",
                    });
                    if (!ok) return;
                  }
                  patchTrial({ enabled: v });
                })();
              }}
            />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Label htmlFor="trial-mode" className="text-xs">Onboarding</Label>
            <Select
              value={trialMode}
              onValueChange={(v) => {
                void (async () => {
                  const next = v as SaasTrialMode;
                  if (next === "self_service" && config.trial?.enabled === true) {
                    const ok = await confirm({
                      title: "¿Pasar a self-service?",
                      description: "Con la prueba prendida, cualquiera que la pida va a tener acceso en el momento, sin pasar por tu revisión.",
                      confirmLabel: "Pasar a self-service",
                    });
                    if (!ok) return;
                  }
                  patchTrial({ mode: next });
                })();
              }}
            >
              <SelectTrigger id="trial-mode" className="h-8 w-72 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TRIAL_MODES.map((m) => <SelectItem key={m} value={m} className="text-xs">{TRIAL_MODE_LABEL[m]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-xs font-medium">Confirmar email</p>
              <p className="text-xs text-muted-foreground">La solicitud llega a la bandeja (o se crea la cuenta) recién cuando la persona hace clic en el link del mail.</p>
            </div>
            <Switch checked={config.trial?.verifyEmail !== false} aria-label="Confirmar email antes de la prueba" onCheckedChange={(v) => patchTrial({ verifyEmail: v })} />
          </div>
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-xs font-medium">Captcha (Cloudflare Turnstile)</p>
              <p className="text-xs text-muted-foreground">Pide el captcha al solicitar la prueba. Prendelo solo con VITE_TURNSTILE_SITE_KEY cargada en el sitio: sin ella nadie puede pedirla.</p>
            </div>
            <Switch checked={config.trial?.captcha === true} aria-label="Captcha al pedir la prueba" onCheckedChange={(v) => patchTrial({ captcha: v })} />
          </div>
          <div className="flex items-center gap-3">
            <Label htmlFor="trial-days" className="text-xs">Días de prueba</Label>
            <Input
              id="trial-days"
              type="number"
              min={TRIAL_DAYS_MIN}
              max={TRIAL_DAYS_MAX}
              className="h-8 w-20 text-xs"
              value={config.trial?.days ?? TRIAL_DAYS_DEFAULT}
              onChange={(e) => {
                const n = Math.round(Number(e.target.value));
                patchTrial({ days: Number.isFinite(n) ? n : TRIAL_DAYS_DEFAULT });
              }}
            />
            <span className="text-xs text-muted-foreground">Entre {TRIAL_DAYS_MIN} y {TRIAL_DAYS_MAX}. Aplica a las próximas cuentas de prueba.</span>
          </div>
        </div>
        <div className="rounded-lg border border-border/50 bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
          <p className="font-medium text-foreground">Pasarelas y prioridad → card <span className="text-primary">Routing</span></p>
          <p className="mt-1">
            Acá solo se enciende o apaga el alta online. Qué métodos se ofrecen y con qué proveedor
            lo define el Routing (operación × método) más las pasarelas configuradas/conectadas.
            No hay un segundo set de toggles de gateway.
          </p>
        </div>
      </div>
      ) : null}

      {showRouting ? (
      <div className="glass-card space-y-4 rounded-xl p-4">
        <p className="text-sm font-semibold">Prioridad por operación × método</p>
        <p className="text-xs text-muted-foreground">
          Mensual exige providers con recurring real. Anual respeta modalidades 12/24/48 del catálogo de planes (sin duplicar pricing acá).
          Packs: crédito, débito y transferencia cuando corresponda.
        </p>
        <div className="rounded-lg border border-border/40 p-3">
          <p className="mb-2 text-xs font-semibold uppercase text-muted-foreground">Estado de las pasarelas</p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {(Object.keys(PROVIDER_HUB) as SaasRoutableProvider[]).map((provider) => {
              const hub = PROVIDER_HUB[provider];
              const configured = availability?.[provider]?.configured;
              return (
                <div key={provider} className="flex items-center justify-between gap-2 rounded-md border border-border/50 px-3 py-2 text-xs">
                  <div>
                    <p className="font-medium">{PROVIDER_LABEL[provider]}</p>
                    <p className={configured ? "text-emerald-600" : "text-amber-600"}>
                      {availability == null ? "—" : configured ? "Conectada" : "Sin conectar"}
                    </p>
                  </div>
                  {hub.tab ? (
                    <a className="text-primary hover:underline" href={`/admin?tab=${hub.tab}`}>Configurar</a>
                  ) : (
                    <span className="max-w-[10rem] text-right text-[10px] text-muted-foreground">{hub.note}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        {OPS.map((op) => (
          <div key={op} className="space-y-2 rounded-lg border border-border/40 p-3">
            <p className="text-xs font-semibold uppercase text-muted-foreground">{OP_LABEL[op]}</p>
            {METHODS.map((method) => {
              const ordered =
                ss?.operationMethodPriority?.[op]?.[method] ??
                DEFAULT_OPERATION_METHOD_PRIORITY[op][method];
              return (
                <div key={method} className="space-y-1">
                  <p className="text-xs font-medium">{SAAS_CHECKOUT_METHOD_LABELS[method]}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {ordered.map((provider, idx) => (
                      <div key={provider} className="flex items-center gap-1 rounded-md border border-border/50 px-2 py-1 text-[11px]">
                        <span className="text-muted-foreground">{idx + 1}.</span>
                        <span>{PROVIDER_LABEL[provider]}</span>
                        {availability && !availability[provider]?.configured ? <span className="text-amber-600">(sin conectar)</span> : null}
                        <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => moveProvider(op, method, provider, -1)} aria-label="Subir">↑</button>
                        <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => moveProvider(op, method, provider, 1)} aria-label="Bajar">↓</button>
                                              <button
                          type="button"
                          className="ml-1 text-muted-foreground hover:text-destructive disabled:cursor-not-allowed disabled:opacity-30"
                          disabled={ordered.length <= 1}
                          title={ordered.length <= 1 ? "Dejá al menos un proveedor (para apagar el alta online usá Self-service)" : "Quitar de la prioridad"}
                          onClick={() => setPriority(op, method, ordered.filter((p) => p !== provider))}
                          aria-label={`Quitar ${PROVIDER_LABEL[provider]}`}
                        >
                          ✕
                        </button>
</div>
                    ))}
                    {eligibleProvidersFor(op, method, availability ?? {}, { requireConfigured: availability != null })
                      .filter((p) => !ordered.includes(p))
                      .map((provider) => (
                      <button
                        key={provider}
                        type="button"
                        className="rounded-md border border-dashed border-border/50 px-2 py-1 text-[11px] text-muted-foreground hover:border-primary/50"
                        onClick={() => setPriority(op, method, [...ordered, provider])}
                      >
                        + {PROVIDER_LABEL[provider]}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
      ) : null}

      {showTransfer ? (
      <>
      <div className="glass-card space-y-3 rounded-xl p-4">
        <p className="text-sm font-semibold">Cuenta bancaria ARS (pesos)</p>
        <p className="text-xs text-muted-foreground">Se muestra cuando el cliente elige transferencia en pesos durante el onboarding.</p>
        <div className="grid gap-2 sm:grid-cols-2">
          <BankField label="Titular" value={ars?.holder ?? ""} onChange={(v) => patchArs("holder", v)} placeholder="Structura SA" />
          <BankField label="CBU" value={ars?.cbu ?? ""} onChange={(v) => patchArs("cbu", v)} placeholder="000000000000000000000" />
          <BankField label="Alias" value={ars?.alias ?? ""} onChange={(v) => patchArs("alias", v)} placeholder="STRUCTURA.ARS" />
          <BankField label="Banco" value={ars?.bank ?? ""} onChange={(v) => patchArs("bank", v)} placeholder="Banco Galicia" />
          <div className="sm:col-span-2 space-y-1">
            <Label className="text-xs">Instrucciones adicionales (opcional)</Label>
            <Input className="h-8 text-xs" value={ars?.notes ?? ""} onChange={(e) => patchArs("notes", e.target.value)} placeholder="Indicar número de pedido en concepto" />
          </div>
        </div>
      </div>
      <div className="glass-card space-y-3 rounded-xl p-4">
        <p className="text-sm font-semibold">Cuenta bancaria USD (dólares)</p>
        <p className="text-xs text-muted-foreground">Legacy. Los planes SaaS se cobran en ARS; esta cuenta ya no se muestra en el onboarding de planes.</p>
        <div className="grid gap-2 sm:grid-cols-2">
          <BankField label="Titular" value={usd?.holder ?? ""} onChange={(v) => patchUsd("holder", v)} placeholder="GASTON FERNANDEZ" />
          <BankField label="CBU (USD)" value={usd?.cbu ?? ""} onChange={(v) => patchUsd("cbu", v)} placeholder="0000000000000000000000" />
          <BankField label="Alias" value={usd?.alias ?? ""} onChange={(v) => patchUsd("alias", v)} placeholder="MI.ALIAS.USD" />
          <BankField label="Banco" value={usd?.bank ?? ""} onChange={(v) => patchUsd("bank", v)} placeholder="Banco Galicia" />
          <div className="sm:col-span-2 space-y-1">
            <Label className="text-xs">Instrucciones (opcional)</Label>
            <Input className="h-8 text-xs" value={usd?.instructions ?? ""} onChange={(e) => patchUsd("instructions", e.target.value)} placeholder="Indicar nombre de negocio en concepto" />
          </div>
        </div>
      </div>
      </>
      ) : null}

      <p className="text-xs text-muted-foreground">
        El descuento anual y la facturación comercial se configuran en <strong>Producto → Planes</strong>, no en Cobros.
      </p>

      <Button onClick={() => void save()} disabled={saving} className="gap-2">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        Guardar configuración
      </Button>
    </div>
    </AdminGlassPanel>
  );
}
